import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import { Chip, ChoiceChips } from '../ui/bits';
import type { BookEdits } from './actions';
import { AUDIENCE_CHOICES, LITERARY_CHOICES, genreChoices, type GenreOption, type Literary } from './genres';
import { MONTHS, asinInLink, copyName, dayText, fuzzyText, numberedText, readsInOrder } from './model';
import { Sheet, Stars } from './reading';
import type { BookData, Precision, Read } from './types';
import { Fold } from './fold';
import { DiffNote } from './differences';
import { coverLink } from '../queue/lookups';
import { FromField, hasFrom, type FromHow } from './gotFrom';

// Book page Part 5, edit mode, first half (Phase 6.1; boards 4e and 5e, with the decisions doc
// winning; agreed with Kristi Oct 27): the Editing bar, About the book (shared facts, as the
// admin), your genres, subgenres and tags, the cover, a read's dates and takeaways, + Add a past
// read and Remove this read. Behind book_page.edit. Every save has Undo in the shared notice.

export const EditingCtx = createContext(false);
export const useEditing = () => useContext(EditingCtx);

/** The teal bar at the top of the page while editing. */
export function EditBar({ title, onDone }: { title: string; onDone: () => void }) {
  return (
    <div class="bk-editbar" role="region" aria-label="Editing">
      <strong>Editing</strong>
      <span class="bk-editbar-title">{title}</span>
      <span class="bk-editbar-hint">Anything with ✎ can be changed. Each change saves at once, with Undo.</span>
      <button type="button" class="btn-main" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

/** One editable row: the value with ✎, or its editor with Save · Cancel. */
function EditRow<T>({
  label,
  value,
  shown,
  empty = 'Not recorded',
  editor,
  save,
  note,
  extra,
}: {
  label: string;
  value: T;
  shown: (v: T) => ComponentChildren;
  empty?: string;
  editor: (draft: T, set: (v: T) => void) => ComponentChildren;
  save: (v: T) => Promise<boolean>;
  note?: string;
  /** Under the value: Different from yours, when a lookup found something else. */
  extra?: ComponentChildren;
}) {
  const [draft, setDraft] = useState<{ v: T } | null>(null);
  const [busy, setBusy] = useState(false);
  const isEmpty = value === null || value === '' || (Array.isArray(value) && value.length === 0);
  return (
    <div class="bk-row bk-editrow">
      <dt>{label}</dt>
      <dd>
        {draft ? (
          <div class="bk-inplace editing" role="group" aria-label={`Edit ${label}`}>
            {editor(draft.v, (v) => setDraft({ v }))}
            <span class="bk-buttons">
              <button
                type="button"
                class="btn-main"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const ok = await save(draft.v);
                  setBusy(false);
                  if (ok) setDraft(null);
                }}
              >
                Save
              </button>
              <button type="button" class="btn-secondary" onClick={() => setDraft(null)}>
                Cancel
              </button>
              {note ? <span class="bk-quiet">{note}</span> : null}
            </span>
          </div>
        ) : (
          <span class="bk-inplace">
            {isEmpty ? <span class="bk-quiet">{empty}</span> : shown(value)}
            <button type="button" class="bk-pencil" aria-label={`Edit ${label}`} title={`Edit ${label}`} onClick={() => setDraft({ v: value })}>
              ✎
            </button>
          </span>
        )}
        {extra}
      </dd>
    </div>
  );
}

// ---------- rough dates ----------

export type Fuzzy = { on: string | null; precision: Precision | null; words: string | null };

/** Day · Month · Year (any left empty) or your own words; "about" for a rough year. */
export function FuzzyEditor({ value, onChange, label, words: withWords = true }: { value: Fuzzy; onChange: (v: Fuzzy) => void; label: string; words?: boolean }) {
  const [y0, m0, d0] = (value.on ?? '').split('-');
  const p = value.precision;
  const [year, setYear] = useState(y0 ?? '');
  const [month, setMonth] = useState(p === 'day' || p === 'month' ? String(Number(m0)) : '');
  const [day, setDay] = useState(p === 'day' ? String(Number(d0)) : '');
  const [about, setAbout] = useState(p === 'about');
  const [words, setWords] = useState(value.words ?? '');
  const emit = (n: { year?: string; month?: string; day?: string; about?: boolean; words?: string }) => {
    const Y = n.year ?? year;
    const M = n.month ?? month;
    const D = n.day ?? day;
    const A = n.about ?? about;
    const W = n.words ?? words;
    const ok = /^\d{4}$/.test(Y);
    const pad = (x: string) => x.padStart(2, '0');
    onChange({
      on: ok ? `${Y}-${pad(M || '1')}-${pad(M && D ? D : '1')}` : null,
      precision: !ok ? null : A ? 'about' : M && D ? 'day' : M ? 'month' : 'year',
      words: W.trim() || null,
    });
  };
  return (
    <span class="bk-fuzzy" role="group" aria-label={label}>
      <select aria-label="Day" value={day} disabled={!month} onChange={(e) => { const v = (e.target as HTMLSelectElement).value; setDay(v); emit({ day: v }); }}>
        <option value="">Day</option>
        {Array.from({ length: 31 }, (_, i) => (
          <option key={i} value={String(i + 1)}>
            {i + 1}
          </option>
        ))}
      </select>
      <select aria-label="Month" value={month} onChange={(e) => { const v = (e.target as HTMLSelectElement).value; setMonth(v); if (!v) setDay(''); emit({ month: v, day: v ? undefined : '' }); }}>
        <option value="">Month</option>
        {MONTHS.map((m, i) => (
          <option key={m} value={String(i + 1)}>
            {m}
          </option>
        ))}
      </select>
      <input type="text" inputMode="numeric" aria-label="Year" placeholder="Year" maxLength={4} size={5} value={year} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setYear(v); emit({ year: v }); }} />
      <label class="bk-check">
        <input type="checkbox" checked={about} onChange={(e) => { const v = (e.target as HTMLInputElement).checked; setAbout(v); emit({ about: v }); }} />
        about
      </label>
      {withWords ? (
        <input type="text" aria-label="In your words" placeholder="Or in your words (only you see this)" maxLength={500} value={words} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setWords(v); emit({ words: v }); }} />
      ) : null}
    </span>
  );
}

const fuzzyShown = (v: Fuzzy) => fuzzyText(v.on, v.precision, v.words);

// ---------- your lists, for the pickers ----------

type Lists = { genres: (GenreOption & { subgenres: { name: string; short: string | null }[] })[]; tags: string[] };

export function useLists(): Lists | null {
  const [lists, setLists] = useState<Lists | null>(null);
  useEffect(() => {
    if (!supabase) return;
    let live = true;
    void Promise.all([
      supabase.from('genres').select('name, short_name, sort_order, hidden, starter:starter_genres(fiction), subgenres(name, short_name, sort_order, hidden)').order('sort_order'),
      supabase.from('tags').select('name, sort_order').order('sort_order'),
    ]).then(([g, t]) => {
      if (!live) return;
      type G = { name: string; short_name: string | null; hidden: boolean; starter: { fiction: Literary } | { fiction: Literary }[] | null; subgenres: { name: string; short_name: string | null; sort_order: number; hidden: boolean }[] };
      setLists({
        genres: ((g.data ?? []) as G[])
          .filter((x) => !x.hidden)
          .map((x) => ({
            name: x.name,
            short: x.short_name,
            literary: (Array.isArray(x.starter) ? x.starter[0]?.fiction : x.starter?.fiction) ?? null,
            subgenres: [...(x.subgenres ?? [])].filter((s) => !s.hidden).sort((a, b) => a.sort_order - b.sort_order).map((s) => ({ name: s.name, short: s.short_name })),
          })),
        tags: ((t.data ?? []) as { name: string }[]).map((x) => x.name),
      });
    });
    return () => {
      live = false;
    };
  }, []);
  return lists;
}

export const nameOf = (shown: string, all: { name: string; short: string | null }[]) =>
  all.find((x) => x.name === shown || x.short === shown)?.name ?? shown;

/** Chips to tick; with `add`, a box for a new name (tags). */
export function ChipsEditor({ options, value, onChange, label, add, pending }: { options: string[]; value: string[]; onChange: (v: string[]) => void; label: string; add?: boolean; /** Review: the chosen chips are a suggestion still waiting (pale, dashed). */ pending?: boolean }) {
  const [fresh, setFresh] = useState('');
  const all = [...options, ...value.filter((v) => !options.includes(v))];
  return (
    <span class="bk-dialog-chips" role="group" aria-label={label}>
      {all.map((o) => {
        const on = value.includes(o);
        return (
          <Chip key={o} small sug={pending} label={o} on={on} onToggle={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])} />
        );
      })}
      {add ? (
        <span class="bk-addtag">
          <input type="text" aria-label="New tag" placeholder="+ New tag" maxLength={100} value={fresh} onInput={(e) => setFresh((e.target as HTMLInputElement).value)} />
          <button
            type="button"
            class="btn-link"
            disabled={!fresh.trim()}
            onClick={() => {
              const t = fresh.trim();
              if (t && !value.includes(t)) onChange([...value, t]);
              setFresh('');
            }}
          >
            Add
          </button>
        </span>
      ) : null}
    </span>
  );
}

/** Names as you type, from the catalog (people or series), for a datalist. */
function useSuggestions(table: 'people' | 'series', q: string): string[] {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    if (!supabase || q.trim().length < 2) return setNames([]);
    let live = true;
    const t = setTimeout(() => {
      void supabase!
        .from(table)
        .select('name')
        .is('merged_into', null)
        .ilike('name', `%${q.trim().replace(/[%_]/g, '')}%`)
        .limit(8)
        .then(({ data }) => live && setNames(((data ?? []) as { name: string }[]).map((x) => x.name)));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [table, q]);
  return names;
}

/** Authors in order: ↑ ↓ to move, ✕ to take one off, a box to add a name (people you have as you type). */
function AuthorsEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [fresh, setFresh] = useState('');
  const names = useSuggestions('people', fresh);
  const move = (i: number, by: number) => {
    const next = [...value];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x!);
    onChange(next);
  };
  return (
    <span class="bk-authors" role="group" aria-label="Authors">
      <ol>
        {value.map((a, i) => (
          <li key={a}>
            {a}
            <button type="button" class="bk-pencil" aria-label={`Move ${a} up`} disabled={i === 0} onClick={() => move(i, -1)}>
              ↑
            </button>
            <button type="button" class="bk-pencil" aria-label={`Move ${a} down`} disabled={i === value.length - 1} onClick={() => move(i, 1)}>
              ↓
            </button>
            <button type="button" class="bk-pencil" aria-label={`Take off ${a}`} disabled={value.length === 1} onClick={() => onChange(value.filter((x) => x !== a))}>
              ✕
            </button>
          </li>
        ))}
      </ol>
      <span class="bk-addtag">
        <input type="text" list="bk-people" aria-label="Add an author" placeholder="+ Add an author" maxLength={300} value={fresh} onInput={(e) => setFresh((e.target as HTMLInputElement).value)} />
        <datalist id="bk-people">
          {names.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <button
          type="button"
          class="btn-link"
          disabled={!fresh.trim()}
          onClick={() => {
            const t = fresh.trim();
            if (t && !value.includes(t)) onChange([...value, t]);
            setFresh('');
          }}
        >
          Add
        </button>
      </span>
    </span>
  );
}

type SeriesValue = { name: string; position: string; label: string } | null;

function SeriesEditor({ value, onChange }: { value: SeriesValue; onChange: (v: SeriesValue) => void }) {
  const v = value ?? { name: '', position: '', label: '' };
  const names = useSuggestions('series', v.name);
  const set = (k: 'name' | 'position' | 'label', x: string) => onChange({ ...v, [k]: x });
  return (
    <span class="bk-series-edit">
      <input type="text" list="bk-series" aria-label="Series name" placeholder="Series name" maxLength={300} value={v.name} onInput={(e) => set('name', (e.target as HTMLInputElement).value)} />
      <datalist id="bk-series">
        {names.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <input type="text" inputMode="decimal" aria-label="Number in the series" placeholder="#" size={4} maxLength={8} value={v.position} onInput={(e) => set('position', (e.target as HTMLInputElement).value)} />
      <input type="text" aria-label="Label (optional)" placeholder="Label, e.g. Book 0.5" maxLength={100} value={v.label} onInput={(e) => set('label', (e.target as HTMLInputElement).value)} />
      {value ? (
        <button type="button" class="btn-link" onClick={() => onChange(null)}>
          Not in a series
        </button>
      ) : null}
    </span>
  );
}

const LITERARY = LITERARY_CHOICES;

// ---------- About the book, editing ----------

/** About the book in edit mode: the shared facts (the admin only for now). Your genres and tags are edited in the shaded box at the top. */
export function EditAbout({ book, edits }: { book: BookData; edits: BookEdits }) {
  const w = book.work;
  const save = (field: string, value: unknown, message: string) => edits.act('book_edit', { field, value }, message);
  const series = book.series[0];
  const text = (field: string, label: string, value: string | null, long?: boolean, empty?: string) => (
    <EditRow<string>
      label={label}
      value={value ?? ''}
      empty={empty}
      shown={(v) => (long ? <span class="bk-summary">{v}</span> : v)}
      editor={(d, set) =>
        long ? (
          <textarea aria-label={label} rows={6} maxLength={20000} value={d} onInput={(e) => set((e.target as HTMLTextAreaElement).value)} />
        ) : (
          <input type="text" aria-label={label} maxLength={1000} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />
        )
      }
      save={(v) => save(field, v.trim() || null, `${label} saved`)}
      note={field === 'summary' && w.summary_source ? 'Saving replaces the lookup’s summary and its source line.' : undefined}
    />
  );
  return (
    <Fold id="about-edit" title="Book details" label="Book details, editing" open={true}>
      <p class="bk-quiet">Title, authors, series and the rest are shared facts: as the admin, your change is for everyone.</p>
      <dl class="bk-rows">
        {text('title', 'Title', w.title)}
        {text('subtitle', 'Subtitle', w.subtitle)}
        <EditRow<string[]>
          label="Authors"
          value={book.authors}
          shown={(v) => v.join(', ')}
          editor={(d, set) => <AuthorsEditor value={d} onChange={set} />}
          save={(v) => save('authors', v.map((name) => ({ name })), 'Authors saved')}
        />
        <EditRow<SeriesValue>
          label="Series"
          value={series ? { name: series.name, position: series.position === null ? '' : String(series.position), label: series.label ?? '' } : null}
          empty="Not in a series"
          shown={(v) => `${v!.name}${v!.label ? ` · ${v!.label}` : v!.position ? ` · Book ${v!.position}` : ''}`}
          editor={(d, set) => <SeriesEditor value={d} onChange={set} />}
          save={(v) =>
            save('series', v && v.name.trim() ? { name: v.name.trim(), position: v.position.trim() || null, label: v.label.trim() || null } : null, 'Series saved')
          }
        />
        <EditRow<string | null>
          label="Literary genre"
          value={w.fiction}
          shown={(v) => LITERARY.find((l) => l.key === v)?.name ?? (v === 'drama' ? 'Drama' : '')}
          editor={(d, set) => (
            <ChoiceChips<string> class="bk-dialog-chips" label="Literary genre" small clearable choices={LITERARY.map((l) => [l.key, l.name] as const)} value={d} onPick={set} />
          )}
          save={(v) => save('literary_genre', v, 'Literary genre saved')}
          extra={<DiffNote book={book} field="fiction" current={w.fiction === 'fiction' ? 'Fiction' : null} use={() => save('literary_genre', 'fiction', 'Literary genre saved')} />}
        />
        <EditRow<string | null>
          label="Audience"
          value={w.audience}
          empty="Not set"
          shown={(v) => AUDIENCE_CHOICES.find((a) => a.key === v)?.name ?? ''}
          editor={(d, set) => (
            <ChoiceChips<string> class="bk-dialog-chips" label="Audience" small clearable choices={AUDIENCE_CHOICES.map((a) => [a.key, a.name] as const)} value={d} onPick={set} />
          )}
          save={(v) => edits.act('work_audience_edit', { value: v }, 'Audience saved')}
        />
        <EditRow<Fuzzy>
          label="First published"
          value={{ on: w.first_published_on, precision: w.first_published_precision, words: w.first_published_words }}
          shown={fuzzyShown}
          editor={(d, set) => <FuzzyEditor label="First published" value={d} onChange={set} />}
          save={(v) => save('first_published', v.on || v.words ? v : null, 'First published saved')}
          extra={
            <DiffNote
              book={book}
              field="first_published_on"
              current={w.first_published_on?.slice(0, 4) ?? null}
              use={(found) => save('first_published', { on: `${found.slice(0, 4)}-01-01`, precision: 'year', words: null }, 'First published saved')}
            />
          }
        />
        {text('summary', 'Summary', w.summary, true, 'Not recorded')}
        <EditRow<string>
          label="Goodreads ID"
          value={w.goodreads_id ?? ''}
          shown={(v) => v}
          editor={(d, set) => (
            <input type="text" inputMode="numeric" aria-label="Goodreads ID" placeholder="The number in goodreads.com/book/show/…" maxLength={200} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />
          )}
          save={(v) => save('goodreads_id', v.match(/(\d+)/)?.[1] ?? null, 'Goodreads ID saved')}
        />
        <EditRow<string>
          label="Cover"
          value={book.coverLink ?? ''}
          empty="From the catalog"
          shown={(v) => <span class="bk-quiet">your own link · {v.replace(/^https:\/\//, '').slice(0, 40)}…</span>}
          editor={(d, set) => <input type="url" aria-label="Cover link" placeholder="https://… an image" maxLength={2048} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />}
          save={(v) => save('cover', v.trim() ? coverLink(v) : null, v.trim() ? 'Cover saved' : 'Cover taken off')}
          note="Only you see this link. A Google Drive share link works."
        />
      </dl>
    </Fold>
  );
}

/**
 * Your genres, subgenres and tags, edited where they're shown: the "Genre · tags" row of the
 * shaded box at the top (Kristi, Oct 27; it was inside About the book, easy to miss).
 */
export function GenresTagsEdit({ book, edits }: { book: BookData; edits: BookEdits }) {
  const lists = useLists();
  const save = (field: string, value: unknown, message: string) => edits.act('book_edit', { field, value }, message);
  // Strict by literary genre (Kristi, Oct 28): Poetry sees its own genres, and so on; a genre the book has stays.
  const genreOptions = lists ? genreChoices(lists.genres, book.work.fiction as Literary | null, book.genres.map((g) => nameOf(g, lists.genres))) : book.genres;
  const genreNames = book.genres.map((g) => (lists ? nameOf(g, lists.genres) : g));
  const subOptions = (lists?.genres ?? []).filter((g) => genreNames.includes(g.name)).flatMap((g) => g.subgenres);
  return (
    <div class="bk-genres-edit" role="group" aria-label="Your genres and tags">
      <dl class="bk-rows">
        <EditRow<string[]>
          label="Genres"
          value={genreNames}
          empty="None yet"
          shown={(v) => v.join(', ')}
          editor={(d, set) => (lists ? <ChipsEditor label="Genres" options={genreOptions} value={d} onChange={set} /> : <span class="bk-quiet">Loading your genres…</span>)}
          save={(v) => save('genres', [...v, ...(book.hiddenGenres ?? [])], 'Genres saved')}
        />
        <EditRow<string[]>
          label="Subgenres"
          value={book.subgenres.map((s) => (lists ? nameOf(s, subOptions) : s))}
          empty={subOptions.length ? "None yet" : 'Pick a genre first'}
          shown={(v) => v.join(', ')}
          editor={(d, set) => <ChipsEditor label="Subgenres" options={subOptions.map((s) => s.name)} value={d} onChange={set} />}
          save={(v) => save('subgenres', v, 'Subgenres saved')}
        />
        <EditRow<string[]>
          label="Tags"
          value={book.tags}
          empty="None yet"
          shown={(v) => v.join(', ')}
          editor={(d, set) => <ChipsEditor label="Tags" options={lists?.tags ?? []} value={d} onChange={set} add />}
          save={(v) => save('tags', v, 'Tags saved')}
        />
      </dl>
    </div>
  );
}

/** For fans of in edit mode: a shared fact (the admin, for now), Undo after saving (0033). */
export function FansOfEdit({ book, edits }: { book: BookData; edits: BookEdits }) {
  return (
    <dl class="bk-rows">
      <EditRow<string>
        label="For fans of"
        value={book.work.for_fans_of ?? ''}
        empty="Not written yet"
        shown={(v) => <span>{v}</span>}
        editor={(d, set) => (
          <textarea aria-label="For fans of" rows={3} maxLength={5000} placeholder="Who it suits, and books it's like" value={d} onInput={(ev) => set((ev.target as HTMLTextAreaElement).value)} />
        )}
        save={(v) => edits.act('for_fans_of_edit', { value: v }, v.trim() ? 'For fans of saved' : 'For fans of cleared')}
        note="A shared fact: as the admin, for everyone."
      />
    </dl>
  );
}

// ---------- a read, editing ----------

/** A read's dates, takeaways and Remove this read, in edit mode. */
export function ReadEdits({ book, read, n, edits }: { book: BookData; read: Read; n: number; edits: BookEdits }) {
  const [asking, setAsking] = useState(false);
  const notes = book.readingNotes.filter((x) => x.read_id === read.id).length + book.quotes.filter((x) => x.read_id === read.id).length;
  return (
    <div class="bk-read-edit">
      <dl class="bk-rows">
        <EditRow<Fuzzy>
          label="Started"
          value={{ on: read.started_on, precision: read.started_precision, words: read.started_words }}
          shown={fuzzyShown}
          editor={(d, set) => <FuzzyEditor label={`Started, Read ${n}`} value={d} onChange={set} />}
          save={(v) => edits.act('read_edit', { read_id: read.id, field: 'started', value: v.on || v.words ? v : null }, 'Started date saved')}
        />
        {read.outcome !== 'open' ? (
          <EditRow<Fuzzy>
            label={read.outcome === 'finished' ? 'Finished' : 'Stopped'}
            value={{ on: read.ended_on, precision: read.ended_precision, words: read.ended_words }}
            shown={fuzzyShown}
            editor={(d, set) => <FuzzyEditor label={`Finished, Read ${n}`} value={d} onChange={set} />}
            save={(v) => edits.act('read_edit', { read_id: read.id, field: 'ended', value: v.on || v.words ? v : null }, 'Date saved')}
          />
        ) : null}
        <EditRow<string>
          label="Key takeaways"
          value={read.takeaways.join('\n\n')}
          empty="None yet"
          shown={(v) => (
            <ul class="bk-takeaways-list">
              {v.split(/\n\s*\n/).map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          )}
          editor={(d, set) => (
            <textarea aria-label={`Key takeaways, Read ${n}`} rows={5} maxLength={20000} placeholder="One takeaway per paragraph" value={d} onInput={(e) => set((e.target as HTMLTextAreaElement).value)} />
          )}
          save={(v) => edits.act('read_edit', { read_id: read.id, field: 'takeaways', value: v.split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean) }, 'Takeaways saved')}
          note="A blank line between takeaways. Only you see these."
        />
      </dl>
      {asking ? (
        <div class="heads-up bk-ask" role="group" aria-label={`Remove Read ${n}?`}>
          Remove Read {n}? Its dates, stars, copies, progress, pauses and takeaways go
          {notes ? `; its ${notes} ${notes === 1 ? 'quote or note stays' : 'quotes and notes stay'} on the book, marked “from a removed read”` : ''}. Undo puts it all back.
          <button type="button" class="btn-main" onClick={() => void edits.act('read_remove', { read_id: read.id }, `Read ${n} removed`)}>
            Remove
          </button>
          <button type="button" class="btn-secondary" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" class="btn-link" onClick={() => setAsking(true)}>
          Remove this read…
        </button>
      )}
    </div>
  );
}

/** + Add a past read (and Move ▾ › Finished… on a book you're not reading, with `move`). */
export function PastReadSheet({ book, edits, onClose, move }: { book: BookData; edits: BookEdits; onClose: () => void; move?: boolean }) {
  const [ended, setEnded] = useState<Fuzzy>({ on: null, precision: null, words: null });
  const [started, setStarted] = useState<Fuzzy>({ on: null, precision: null, words: null });
  const [withStart, setWithStart] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [copies, setCopies] = useState<string[]>([]);
  const [again, setAgain] = useState(false);
  const [busy, setBusy] = useState(false);
  const count = readsInOrder(book.reads).length;
  const save = async () => {
    setBusy(true);
    const details: Record<string, unknown> = { rating, copies, read_again: again, move: Boolean(move) };
    if (ended.on || ended.words) details.ended = ended;
    if (withStart && (started.on || started.words)) details.started = started;
    const ok = await edits.act('read_add_past', { details }, move ? `${book.work.title} moved to Finished` : 'Past read added');
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet title={move ? `Finished ${book.work.title}` : `Add a past read of ${book.work.title}`} sub={count ? `It joins your ${count} ${count === 1 ? 'read' : 'reads'} in My reads.` : 'A read you finished before, with a rough date if that’s all you have.'} onClose={onClose}>
      <div class="bk-field">
        <span class="bk-label">When you finished it</span>
        <FuzzyEditor label="Finished" value={ended} onChange={setEnded} />
      </div>
      {withStart ? (
        <div class="bk-field">
          <span class="bk-label">When you started it</span>
          <FuzzyEditor label="Started" value={started} onChange={setStarted} />
        </div>
      ) : (
        <button type="button" class="btn-link" onClick={() => setWithStart(true)}>
          + When you started it
        </button>
      )}
      <Stars value={rating} onChange={setRating} hint />
      {book.copies.length ? (
        <div class="bk-dialog-chips" role="group" aria-label="Copies you used">
          {book.copies.map((c) => {
            const on = copies.includes(c.id);
            return (
              <Chip key={c.id} small label={copyName(book, c)} on={on} onToggle={() => setCopies(on ? copies.filter((x) => x !== c.id) : [...copies, c.id])} />
            );
          })}
        </div>
      ) : null}
      <label class="bk-check">
        <input type="checkbox" checked={again} onChange={(e) => setAgain((e.target as HTMLInputElement).checked)} />
        Read again in future?
      </label>
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
          {move ? 'Move to Finished' : 'Add the read'}
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

// ---------- second half: copies, the library, quotes and notes, Remove this book ----------

/**
 * Names from a pasted list (Kristi, Oct 27: a full cast copied from Amazon or Audible): links keep
 * only their text, "(Narrator)" and other role labels and a leading "Narrated by:" go, and the
 * names split on commas, semicolons, new lines, "and" and "&"; each name once, in order.
 */
export function namesFrom(text: string): string[] {
  const plain = text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/\((?:narrator|narrators|reader|read by|author|translator|foreword|introduction)[^)]*\)/gi, ',')
    .replace(/^\s*(?:narrated|read)\s+by\s*:?/i, '');
  const out: string[] = [];
  for (const part of plain.split(/\s*(?:[,;\n]|\s&\s|\sand\s)\s*/i)) {
    const n = part.replace(/\s+/g, ' ').trim();
    if (n && !out.some((x) => x.toLowerCase() === n.toLowerCase())) out.push(n);
  }
  return out;
}

const hm = (m: number | null) => (m === null ? '' : `${Math.floor(m / 60)}:${String(Math.round(m % 60)).padStart(2, '0')}`);
const minutesOf = (t: string): number | null => {
  const x = /^\s*(\d+)\s*[:h]\s*(\d{1,2})\s*m?\s*$/i.exec(t);
  return x ? Number(x[1]) * 60 + Number(x[2]) : /^\s*\d+\s*$/.test(t) ? Number(t) * 60 : null;
};
const LISTEN = new Set(['audible', 'librofm', 'libby']);
const SHARED = 'A shared fact: as the admin, for everyone.';
const ISBN_NOTE = 'A shared fact: as the admin, this changes the ISBN for everyone with this edition.';

/** A pasted library Kindle link, with the Kindle ID Open in Kindle reads from it. */
function KindleLinkShown({ url }: { url: string }) {
  const asin = asinInLink(url);
  return (
    <span class="bk-kindle-link">
      <span class="bk-quiet">{url.replace(/^https:\/\//, '').slice(0, 44)}…</span>
      <span class={asin ? '' : 'bk-warn-text'}>{asin ? `Opens in Kindle as ${asin}` : 'No Kindle ID found in this link'}</span>
    </span>
  );
}

/** "412 of 1,000": the number and the total, either may be left empty. */
function NumberedEditor({ value, onChange }: { value: { number: string; of: string }; onChange: (v: { number: string; of: string }) => void }) {
  return (
    <span class="bk-numbered" role="group" aria-label="Numbered">
      <input type="text" inputMode="numeric" aria-label="Number" placeholder="412" maxLength={9} size={6} value={value.number} onInput={(e) => onChange({ ...value, number: (e.target as HTMLInputElement).value })} />
      <span>of</span>
      <input type="text" inputMode="numeric" aria-label="Of how many" placeholder="1,000" maxLength={9} size={7} value={value.of} onInput={(e) => onChange({ ...value, of: (e.target as HTMLInputElement).value })} />
    </span>
  );
}
const STATES: { key: 'have_it' | 'lent_out' | 'lost' | 'donated'; name: string }[] = [
  { key: 'have_it', name: 'Have it' },
  { key: 'lent_out', name: 'Lent out' },
  { key: 'lost', name: 'Lost' },
  { key: 'donated', name: 'Donated' },
];

const one = (label: string, value: string, set: (v: string) => void, opts: { placeholder?: string; inputMode?: 'numeric' | 'decimal'; max?: number } = {}) => (
  <input type="text" aria-label={label} placeholder={opts.placeholder} inputMode={opts.inputMode} maxLength={opts.max ?? 1000} value={value} onInput={(e) => set((e.target as HTMLInputElement).value)} />
);

/** A copy in edit mode: its details, and Remove a copy… (with what goes with it). */
export function CopyEdits({ book, copy, edits }: { book: BookData; copy: import('./types').Copy; edits: BookEdits }) {
  const [asking, setAsking] = useState(false);
  const save = (field: string, value: unknown, message = 'Copy saved') => edits.act('copy_edit', { copy: copy.id, field, value }, message);
  const library = copy.how === 'library_loan';
  const listen = LISTEN.has(copy.format);
  const edition = book.editions.find((e) => e.id === copy.edition_id);
  const usedBy = readsInOrder(book.reads)
    .map((r, i) => ({ r, n: i + 1 }))
    .filter(({ r }) => r.copies.some((c) => c.copy_id === copy.id));
  const loanCount = copy.loans.length;
  const goes = [
    usedBy.length ? `it comes off ${usedBy.map((u) => `Read ${u.n}`).join(' and ')} (the ${usedBy.length === 1 ? 'read stays' : 'reads stay'})` : null,
    loanCount ? `its ${loanCount} ${loanCount === 1 ? 'loan' : 'loans'} and their library history go too` : null,
  ].filter(Boolean);
  return (
    <div class="bk-copy-edit">
      <dl class="bk-rows">
        {listen ? (
          <EditRow<string>
            label="Listening length"
            value={hm(copy.listening_minutes ?? edition?.listening_minutes ?? null)}
            empty="Not recorded · add it to enter a time in Progress"
            shown={(v) => v.replace(':', ' h ') + ' min'}
            editor={(d, set) => one('Listening length (h:mm)', d, set, { placeholder: 'h:mm, e.g. 12:44', max: 10 })}
            save={(v) => save('listening_minutes', v.trim() ? minutesOf(v) : null, 'Listening length saved')}
          />
        ) : (
          <EditRow<string>
            label="Pages"
            value={String(copy.pages ?? edition?.pages ?? '')}
            empty="Not recorded · add it to enter a page in Progress"
            shown={(v) => v}
            editor={(d, set) => one('Pages', d, set, { inputMode: 'numeric', max: 6 })}
            save={(v) => save('pages', v.trim() ? Number(v) : null, 'Pages saved')}
            extra={
              edition ? (
                <DiffNote book={book} field="pages" rowId={edition.id} current={edition.pages ? String(edition.pages) : null} use={(found) => save('edition_pages', Number(found), 'Edition’s pages saved')} />
              ) : null
            }
          />
        )}
        {listen ? (
          <EditRow<string>
            label="Narrator"
            value={(edition?.narrators ?? []).join(', ')}
            empty="Not recorded"
            shown={(v) => v}
            editor={(d, set) => (
              <>
                <textarea
                  aria-label="Narrators"
                  rows={3}
                  maxLength={6000}
                  placeholder="Names with commas between, or paste the list from Audible or Amazon"
                  value={d}
                  onInput={(e) => set((e.target as HTMLTextAreaElement).value)}
                  onBlur={() => set(namesFrom(d).join(', '))}
                />
                {namesFrom(d).length > 1 ? <span class="bk-quiet">{namesFrom(d).length} narrators</span> : null}
              </>
            )}
            save={(v) => save('narrator', namesFrom(v), 'Narrator saved')}
            note="A shared fact: as the admin, for everyone."
          />
        ) : null}
        <EditRow<string>
          label="Publisher"
          value={edition?.publisher ?? ''}
          shown={(v) => v}
          editor={(d, set) => one('Publisher', d, set, { placeholder: 'Easton Press', max: 300 })}
          save={(v) => save('publisher', v.trim() || null, 'Publisher saved')}
          note={SHARED}
        />
        <EditRow<string>
          label="Imprint or series"
          value={edition?.imprint ?? ''}
          shown={(v) => v}
          editor={(d, set) => one('Imprint or series', d, set, { placeholder: 'Vintage Contemporaries', max: 300 })}
          save={(v) => save('imprint', v.trim() || null, 'Imprint or series saved')}
          note={SHARED}
        />
        <EditRow<string>
          label="Edition"
          value={edition?.edition_statement ?? ''}
          shown={(v) => v}
          editor={(d, set) => one('Edition', d, set, { placeholder: 'Collector’s edition', max: 300 })}
          save={(v) => save('edition_statement', v.trim() || null, 'Edition saved')}
          note={SHARED}
        />
        <EditRow<Fuzzy>
          label="Published"
          value={{ on: edition?.published_on ?? null, precision: edition?.published_precision ?? null, words: null }}
          shown={(v) => fuzzyShown(v) || <span class="bk-quiet">Not recorded</span>}
          editor={(d, set) => <FuzzyEditor label="Published" value={d} onChange={set} words={false} />}
          save={(v) => save('published', v.on ? { on: v.on, precision: v.precision } : null, 'Published date saved')}
          note={SHARED}
        />
        {copy.format === 'paper' ? (
          <>
            {/* Editable for the admin from Oct 30 (Kristi, changing Oct 29), with the reminder kept. */}
            <EditRow<string>
              label="ISBN"
              value={edition?.isbn13 ?? ''}
              shown={(v) => v}
              editor={(d, set) => one('ISBN', d, set, { placeholder: 'ISBN-10 or ISBN-13', max: 20 })}
              save={(v) => save('isbn', v.trim(), v.trim() ? 'ISBN saved' : 'ISBN taken off')}
              note={ISBN_NOTE}
              extra={<DiffNote book={book} field="isbn13" rowId={edition?.id} />}
            />
            <EditRow<boolean>
              label="Signed"
              value={copy.signed}
              shown={(v) => (v ? 'Signed' : 'Not signed')}
              editor={(d, set) => (
                <ChoiceChips<boolean>
                  class="bk-dialog-chips"
                  label="Signed"
                  small
                  choices={[
                    [true, 'Signed'],
                    [false, 'Not signed'],
                  ]}
                  value={d}
                  onPick={(x) => x !== null && set(x)}
                />
              )}
              save={(v) => save('signed', v, v ? 'Marked signed' : 'Marked not signed')}
            />
            <EditRow<{ number: string; of: string }>
              label="Numbered"
              value={{ number: copy.number_in_run ? String(copy.number_in_run) : '', of: copy.run_size ? String(copy.run_size) : '' }}
              shown={(v) => numberedText(Number(v.number) || null, Number(v.of) || null) || <span class="bk-quiet">Not numbered</span>}
              editor={(d, set) => <NumberedEditor value={d} onChange={set} />}
              save={(v) => save('numbered', v.number.trim() || v.of.trim() ? { number: v.number, of: v.of } : null, 'Numbered saved')}
            />
          </>
        ) : null}
        {!library ? (
          <EditRow<string>
            label="How"
            value={copy.how}
            shown={(v) => ({ bought: 'Bought', gift: 'A gift', shared: 'Shared with me', free: 'Free' })[v] ?? v}
            editor={(d, set) => (
              <ChoiceChips<string>
                class="bk-dialog-chips"
                label="How"
                small
                choices={[
                  ['bought', 'I bought it'],
                  ['gift', 'A gift'],
                  ['shared', 'Shared with me'],
                  ['free', 'Free'],
                ]}
                value={d}
                onPick={(h) => h && set(h)}
              />
            )}
            save={(v) => save('how', v)}
            note={hasFrom(copy.how) ? 'A new How clears From.' : undefined}
          />
        ) : null}
        {hasFrom(copy.how) ? (
          <EditRow<string>
            label="From"
            value={copy.got_from ?? ''}
            shown={(v) => v}
            editor={(d, set) => <FromField how={copy.how as FromHow} value={d} onChange={set} cls="bk-from-edit" />}
            save={(v) => save('got_from', v.trim() || null, 'From saved')}
            note="Only you see this."
          />
        ) : null}
        {copy.how === 'bought' ? (
          <>
            <EditRow<string> label="Bought from" value={copy.store ?? ''} shown={(v) => v} editor={(d, set) => one('Bought from', d, set, { max: 100 })} save={(v) => save('store', v.trim() || null)} />
            <EditRow<string>
              label="Price"
              value={copy.price ?? ''}
              shown={(v) => `$${v}`}
              editor={(d, set) => one('Price', d, set, { inputMode: 'decimal', placeholder: '2.99', max: 10 })}
              save={(v) => save('price', v.replace(/^\$/, '').trim() || null)}
              note="Only you see this."
            />
          </>
        ) : null}
        {!library ? (
          <>
            <EditRow<string>
              label="Found through"
              value={copy.found_through ?? ''}
              shown={(v) => v}
              editor={(d, set) => one('Found through', d, set, { placeholder: 'BookBub, Kindle Daily Deal…', max: 100 })}
              save={(v) => save('found_through', v.trim() || null)}
              note="Only you see this."
            />
            <EditRow<Fuzzy>
              label="Acquired"
              value={{ on: copy.acquired_on, precision: copy.acquired_precision, words: copy.acquired_words }}
              shown={fuzzyShown}
              editor={(d, set) => <FuzzyEditor label="Acquired" value={d} onChange={set} />}
              save={(v) => save('acquired', v.on || v.words ? v : null)}
            />
            <EditRow<string>
              label="Where it is"
              value={copy.state}
              shown={(v) => STATES.find((s) => s.key === v)?.name ?? v}
              editor={(d, set) => (
                <ChoiceChips<string>
                  class="bk-dialog-chips"
                  label="Where it is"
                  small
                  choices={STATES.map((s) => [s.key, s.key === 'have_it' && copy.state !== 'have_it' ? 'Got it back' : s.name] as const)}
                  value={d}
                  onPick={(k) => k && set(k)}
                />
              )}
              save={(v) => save('state', v)}
            />
          </>
        ) : null}
        {library && copy.format === 'kindle' ? (
          <EditRow<string>
            label="Kindle link (from Libby)"
            value={copy.library_kindle_url ?? ''}
            empty={edition?.asin ? `None pasted · Open in Kindle uses the book’s Kindle ID ${edition.asin}` : 'None pasted · paste the link Amazon shows after you borrow it'}
            shown={(v) => <KindleLinkShown url={v} />}
            editor={(d, set) => (
              <>
                <input type="url" aria-label="Kindle link (from Libby)" placeholder="https://www.amazon.com/…" maxLength={2048} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />
                <span class="bk-quiet">{d.trim() ? (asinInLink(d) ? `Opens in Kindle as ${asinInLink(d)}` : 'No Kindle ID found in this link') : 'After a new checkout or renewal, paste the link Amazon shows.'}</span>
              </>
            )}
            save={(v) => save('link', v.trim() || null, 'Kindle link saved')}
            note="Only you see this link."
          />
        ) : !library ? (
          <EditRow<string>
            label="Link"
            value={copy.link ?? ''}
            shown={(v) => <span class="bk-quiet">{v.replace(/^https:\/\//, '').slice(0, 44)}…</span>}
            editor={(d, set) => <input type="url" aria-label="Link" placeholder="https://…" maxLength={2048} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />}
            save={(v) => save('link', v.trim() || null)}
            note="Only you see this link."
          />
        ) : null}
        {!library && (copy.format === 'kindle' || copy.format === 'audible') ? (
          <EditRow<string>
            label={copy.format === 'kindle' ? 'Kindle ID' : 'Audible ID'}
            value={(copy.format === 'kindle' ? edition?.asin : edition?.audible_id) ?? ''}
            shown={(v) => v}
            editor={(d, set) => one(copy.format === 'kindle' ? 'Kindle ID' : 'Audible ID', d, set, { placeholder: copy.format === 'kindle' ? 'B0…' : '', max: 14 })}
            save={(v) => save(copy.format === 'kindle' ? 'asin' : 'audible_id', v.trim() || null)}
            note="A shared fact: as the admin, for everyone."
          />
        ) : null}
        <EditRow<string>
          label="Note about the copy"
          value={copy.note ?? ''}
          shown={(v) => <span class="bk-note">{v}</span>}
          editor={(d, set) => <textarea aria-label="Note about the copy" rows={3} maxLength={5000} value={d} onInput={(e) => set((e.target as HTMLTextAreaElement).value)} />}
          save={(v) => save('note', v.trim() || null)}
          note="Only you see this."
        />
      </dl>
      {asking ? (
        <div class="heads-up bk-ask" role="group" aria-label={`Remove ${copyName(book, copy)}?`}>
          Remove {copyName(book, copy)}?{goes.length ? ` ${goes.join('; ')}.` : ''} Undo puts it all back.
          <button type="button" class="btn-main" onClick={() => void edits.act('copy_remove', { copy: copy.id }, `${copyName(book, copy)} removed`)}>
            Remove
          </button>
          <button type="button" class="btn-secondary" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" class="btn-link" onClick={() => setAsking(true)}>
          Remove a copy…
        </button>
      )}
    </div>
  );
}

const LOAN_STEPS: { key: import('./types').LoanStep; name: string }[] = [
  { key: 'on_hold', name: 'On hold' },
  { key: 'ready', name: 'Ready' },
  { key: 'borrowed', name: 'Borrowed' },
  { key: 'returned', name: 'Returned' },
  { key: 'cancelled', name: 'Cancelled' },
];
const LOAN_DATES: { key: 'hold_placed_on' | 'ready_on' | 'borrowed_on' | 'due_on' | 'returned_on'; name: string }[] = [
  { key: 'hold_placed_on', name: 'Hold placed' },
  { key: 'ready_on', name: 'Ready' },
  { key: 'borrowed_on', name: 'Borrowed' },
  { key: 'due_on', name: 'Due' },
  { key: 'returned_on', name: 'Returned' },
];

/** A library loan in edit mode: its step, its dates. */
export function LoanEdits({ loan, edits }: { loan: import('./types').Loan; edits: BookEdits }) {
  const save = (field: string, value: unknown, message: string) => edits.act('loan_edit', { loan: loan.id, field, value }, message);
  return (
    <dl class="bk-rows bk-loan-edit">
      <EditRow<string>
        label="Library step"
        value={loan.step}
        shown={(v) => LOAN_STEPS.find((s) => s.key === v)?.name ?? v}
        editor={(d, set) => (
          <ChoiceChips<string> class="bk-dialog-chips" label="Library step" small choices={LOAN_STEPS.map((s) => [s.key, s.name] as const)} value={d} onPick={(k) => k && set(k)} />
        )}
        save={(v) => save('step', v, 'Library step saved')}
      />
      {LOAN_DATES.map((x) => (
        <EditRow<string>
          key={x.key}
          label={x.name}
          value={loan[x.key] ?? ''}
          shown={(v) => dayText(v)}
          editor={(d, set) => <input type="date" aria-label={x.name} value={d} onInput={(e) => set((e.target as HTMLInputElement).value)} />}
          save={(v) => save(x.key, v || null, `${x.name} date saved`)}
        />
      ))}
    </dl>
  );
}

/** A Library history row in edit mode: Change the date · This didn't happen. */
export function EventEdits({ event, edits }: { event: import('./types').LibraryEvent; edits: BookEdits }) {
  const [date, setDate] = useState<string | null>(null);
  return date !== null ? (
    <span class="bk-event-edit">
      <input type="date" aria-label="Date of this step" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
      <button type="button" class="btn-main" disabled={!date} onClick={async () => (await edits.act('loan_event_edit', { event: event.id, happened_on: date }, 'Date changed')) && setDate(null)}>
        Save
      </button>
      <button type="button" class="btn-secondary" onClick={() => setDate(null)}>
        Cancel
      </button>
    </span>
  ) : (
    <span class="bk-event-edit">
      <button type="button" class="btn-link" onClick={() => setDate(event.happened_on ?? '')}>
        Change the date
      </button>
      <button type="button" class="btn-link" onClick={() => void edits.act('loan_event_remove', { event: event.id }, 'Taken out of the history')}>
        This didn’t happen
      </button>
    </span>
  );
}

/** A reading note or quote in edit mode: ✎ to change it, Delete. */
export function EntryEdits({ kind, entry, edits }: { kind: 'note' | 'quote'; entry: { id: string; body: string; location: string | null; chapter?: string | null }; edits: BookEdits }) {
  const [draft, setDraft] = useState<{ body: string; location: string; chapter: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const what = kind === 'note' ? 'reading note' : 'quote';
  if (draft)
    return (
      <div class="bk-inplace editing" role="group" aria-label={`Edit ${what}`}>
        <textarea aria-label={kind === 'note' ? 'Reading note' : 'Quote'} rows={4} maxLength={20000} value={draft.body} onInput={(e) => setDraft({ ...draft, body: (e.target as HTMLTextAreaElement).value })} />
        <input type="text" aria-label="Location" placeholder="Page or location" maxLength={200} value={draft.location} onInput={(e) => setDraft({ ...draft, location: (e.target as HTMLInputElement).value })} />
        {kind === 'quote' ? (
          <input type="text" aria-label="Chapter" placeholder="Chapter or part" maxLength={300} value={draft.chapter} onInput={(e) => setDraft({ ...draft, chapter: (e.target as HTMLInputElement).value })} />
        ) : null}
        <span class="bk-buttons">
          <button
            type="button"
            class="btn-main"
            disabled={!draft.body.trim()}
            onClick={async () => {
              const ok =
                kind === 'note'
                  ? await edits.act('note_edit', { note: entry.id, body: draft.body, location: draft.location || null }, 'Reading note saved')
                  : await edits.act('quote_edit', { quote: entry.id, body: draft.body, location: draft.location || null, chapter: draft.chapter || null }, 'Quote saved');
              if (ok) setDraft(null);
            }}
          >
            Save
          </button>
          <button type="button" class="btn-secondary" onClick={() => setDraft(null)}>
            Cancel
          </button>
        </span>
      </div>
    );
  return (
    <span class="bk-entry-edit">
      <button type="button" class="bk-pencil" aria-label={`Edit this ${what}`} onClick={() => setDraft({ body: entry.body, location: entry.location ?? '', chapter: entry.chapter ?? '' })}>
        ✎
      </button>
      {asking ? (
        <>
          <button
            type="button"
            class="btn-link"
            onClick={() => void (kind === 'note' ? edits.act('note_remove', { note: entry.id }, 'Reading note deleted') : edits.act('quote_remove', { quote: entry.id }, 'Quote deleted'))}
          >
            Delete it
          </button>
          <button type="button" class="btn-link" onClick={() => setAsking(false)}>
            Keep it
          </button>
        </>
      ) : (
        <button type="button" class="btn-link" onClick={() => setAsking(true)}>
          Delete…
        </button>
      )}
    </span>
  );
}

/** Remove this book… at the foot of the page in edit mode, saying what goes with it. */
export function RemoveBook({ book, edits, onRemoved }: { book: BookData; edits: BookEdits; onRemoved: () => void }) {
  const [asking, setAsking] = useState(false);
  const counts = [
    [book.reads.length, 'read', 'reads'],
    [book.copies.length, 'copy', 'copies'],
    [book.quotes.length, 'quote', 'quotes'],
    [book.readingNotes.length, 'reading note', 'reading notes'],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, a, b]) => `${n} ${n === 1 ? a : b}`);
  const loans = book.copies.some((c) => c.loans.length);
  return (
    <div class="bk-remove-book">
      {asking ? (
        <div class="heads-up bk-ask" role="group" aria-label={`Remove ${book.work.title}?`}>
          Remove {book.work.title} from your list? Everything on it goes with it
          {counts.length ? `: ${counts.join(', ')}${loans ? ', its library history' : ''}` : ''}. Undo brings it all back within a day; after that it’s gone for good.
          <button
            type="button"
            class="btn-main"
            onClick={async () => {
              if (await edits.act('book_remove', {}, `${book.work.title} removed from your list`)) onRemoved();
            }}
          >
            Remove the book
          </button>
          <button type="button" class="btn-secondary" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" class="btn-link" onClick={() => setAsking(true)}>
          Remove this book…
        </button>
      )}
    </div>
  );
}
